import React from 'react';
import { useId } from 'react';
import { ColorSwatch } from './ColorSwatch';
import './FmRankedList.css';

export interface FmRankedListItem {
  key: string;
  label: React.ReactNode;
  /** Right-aligned primary value (already formatted string or node). */
  value?: React.ReactNode;
  /** Explicit rank; if omitted, uses 1-based position after any limit slice. */
  rank?: number;
  /** Change/status indicator. 'up'|'down'|'flat' render an arrow; a number renders the delta beside it. */
  indicator?: 'up' | 'down' | 'flat';
  /** Optional numeric delta shown next to the arrow (e.g. 1 -> "↑1"). */
  delta?: number;
  /** Highlight this row with a right-edge accent bar. */
  accent?: boolean;
  /** Controlled selection state for ranked rows paired with another view. */
  selected?: boolean;
  /** Optional secondary/sub label under the main label. */
  sublabel?: React.ReactNode;
  /** A data colour (a palette `var(--fm-species-*)`) shown as a swatch before the label. */
  swatch?: string;
  href?: string;
  onClick?: () => void;
  'data-testid'?: string;
}

export interface FmRankedListProps {
  items: FmRankedListItem[];
  /** Cap the number of visible rows. Omit = show all. Items may be any length; component slices to `limit`. */
  limit?: number;
  /**
   * Colour-grade the top N rows as a highlighted group: a fading accent tint
   * (row 1 strongest) plus a continuous trailing accent bar spanning the group.
   * Omit/0 = no grading.
   */
  highlightTop?: number;
  eyebrow?: React.ReactNode;
  title?: React.ReactNode;
  /**
   * Where the eyebrow/title/controls sit. 'top' (default) stacks them above the
   * list; 'left' places them as their own left column (ranked layout)
   * — list in the middle, narrative on the right.
   */
  headerSide?: 'top' | 'left';
  /** Header label above the value column (e.g. "Revenue"). */
  valueHeader?: React.ReactNode;
  /** Optional controls slot (segmented toggle, filter) rendered under the title. */
  controls?: React.ReactNode;
  /** Optional narrative panel beside the list (bulleted prose etc.). */
  narrative?: React.ReactNode;
  /** Side the narrative sits on (desktop). Default 'right'. */
  narrativeSide?: 'left' | 'right';
  /** Shown when items is empty. */
  emptyLabel?: React.ReactNode;
  tone?: 'light' | 'dark';
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'data-testid'?: string;
}

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

interface RankedListClassArgs {
  tone: 'light' | 'dark';
  headerLeft: boolean;
  hasNarrative: boolean;
  narrativeSide: 'left' | 'right';
  className: string | undefined;
}

function buildRankedListClass(args: RankedListClassArgs): string {
  const { tone, headerLeft, hasNarrative, narrativeSide, className } = args;
  return cx(
    'fm-ranked-list',
    tone === 'dark' && 'fm-ranked-list--dark',
    headerLeft && 'fm-ranked-list--header-left',
    hasNarrative && 'fm-ranked-list--with-narrative',
    hasNarrative && !headerLeft && narrativeSide === 'left' && 'fm-ranked-list--narrative-left',
    className,
  );
}

const INDICATOR_GLYPH: Record<NonNullable<FmRankedListItem['indicator']>, string> = {
  up: '▲',
  down: '▼',
  flat: '→',
};

/** Render the change/status indicator glyph + optional delta for a row. */
const RowIndicator: React.FC<{
  indicator: NonNullable<FmRankedListItem['indicator']>;
  delta?: number;
}> = ({ indicator, delta }) => (
  <span
    className={cx('fm-ranked-list__indicator', `fm-ranked-list__indicator--${indicator}`)}
    aria-hidden="true"
  >
    <span className="fm-ranked-list__indicator-glyph">{INDICATOR_GLYPH[indicator]}</span>
    {delta !== undefined ? (
      <span className="fm-ranked-list__indicator-delta">{delta}</span>
    ) : null}
  </span>
);

interface RowInnerProps {
  rank: number;
  item: FmRankedListItem;
  showIndicators: boolean;
}

/** The shared row content, independent of the interactive wrapper element. */
const RowInner: React.FC<RowInnerProps> = ({ rank, item, showIndicators }) => (
  <>
    <span className="fm-ranked-list__rank" aria-hidden="true">
      {rank}
    </span>
    {showIndicators ? (
      <span className="fm-ranked-list__indicator-col">
        {item.indicator ? (
          <RowIndicator indicator={item.indicator} delta={item.delta} />
        ) : null}
      </span>
    ) : null}
    {item.swatch ? <ColorSwatch color={item.swatch} className="fm-ranked-list__swatch" /> : null}
    <span className="fm-ranked-list__labels">
      <span className="fm-ranked-list__label">{item.label}</span>
      {item.sublabel ? (
        <span className="fm-ranked-list__sublabel">{item.sublabel}</span>
      ) : null}
    </span>
    {item.value !== undefined ? (
      <span className="fm-ranked-list__value">{item.value}</span>
    ) : null}
  </>
);

interface RankedRowProps {
  rank: number;
  item: FmRankedListItem;
  showIndicators: boolean;
  /** When set, this row is in the highlighted top group; value is the tint % (row 1 strongest). */
  topShade?: number;
}

/**
 * One ranked row. The whole row is the click target: an `<a>` when `href` is
 * set, a `<button>` when only `onClick` is set, otherwise a plain `<div>`.
 */
const RankedRow: React.FC<RankedRowProps> = ({ rank, item, showIndicators, topShade }) => {
  const isTop = topShade !== undefined;
  const rowClass = cx(
    'fm-ranked-list__row',
    showIndicators && 'fm-ranked-list__row--with-indicators',
    (item.accent || item.selected) && 'fm-ranked-list__row--accent',
    isTop && 'fm-ranked-list__row--top',
    (item.href || item.onClick) && 'fm-ranked-list__row--interactive',
  );
  // Grade tint via a CSS custom property (token-based color-mix in the CSS).
  const rowStyle = isTop
    ? ({ ['--rank-top-shade' as string]: String(topShade) } as React.CSSProperties)
    : undefined;
  const inner = <RowInner rank={rank} item={item} showIndicators={showIndicators} />;

  if (item.href) {
    return (
      <li className="fm-ranked-list__li">
        <a
          className={rowClass}
          style={rowStyle}
          href={item.href}
          onClick={item.onClick}
          aria-current={item.selected ? 'true' : undefined}
          data-testid={item['data-testid']}
        >
          {inner}
        </a>
      </li>
    );
  }

  if (item.onClick) {
    return (
      <li className="fm-ranked-list__li">
        <button
          type="button"
          className={rowClass}
          style={rowStyle}
          onClick={item.onClick}
          aria-pressed={item.selected}
          data-testid={item['data-testid']}
        >
          {inner}
        </button>
      </li>
    );
  }

  return (
    <li className="fm-ranked-list__li">
      <div className={rowClass} style={rowStyle} data-testid={item['data-testid']}>
        {inner}
      </div>
    </li>
  );
};

interface RankedHeaderProps {
  headerId?: string;
  eyebrow?: React.ReactNode;
  title?: React.ReactNode;
  controls?: React.ReactNode;
}

/** The section header: eyebrow, title, and an optional controls slot. */
const RankedHeader: React.FC<RankedHeaderProps> = ({
  headerId,
  eyebrow,
  title,
  controls,
}) => {
  if (!eyebrow && !title && !controls) {
    return null;
  }
  return (
    <header className="fm-ranked-list__header">
      {eyebrow ? <span className="fm-ranked-list__eyebrow">{eyebrow}</span> : null}
      {title ? (
        <h2 id={headerId} className="fm-ranked-list__title">
          {title}
        </h2>
      ) : null}
      {controls ? <div className="fm-ranked-list__controls">{controls}</div> : null}
    </header>
  );
};

// Strongest tint (%) applied to the #1 row of a highlighted top group; lower
// ranks fade linearly toward 0. Mixed from the full accent, so a modest % reads
// clearly.
const TOP_SHADE_MAX = 18;

interface RankedListColProps {
  visible: FmRankedListItem[];
  hiddenCount: number;
  valueHeader?: React.ReactNode;
  emptyLabel?: React.ReactNode;
  showIndicators: boolean;
  highlightTop: number;
}

/** The value-column header, the `<ol>` of ranked rows, and the "+N more" footer. */
const RankedListCol: React.FC<RankedListColProps> = ({
  visible,
  hiddenCount,
  valueHeader,
  emptyLabel,
  showIndicators,
  highlightTop,
}) => {
  if (visible.length === 0) {
    return (
      <div className="fm-ranked-list__empty">{emptyLabel ?? 'No items to rank.'}</div>
    );
  }
  return (
    <div className={cx('fm-ranked-list__list-col', showIndicators && 'fm-ranked-list__list-col--with-indicators')}>
      {valueHeader ? (
        <div className="fm-ranked-list__value-header" aria-hidden="true">
          <span className="fm-ranked-list__value-header-label">{valueHeader}</span>
        </div>
      ) : null}
      <ol className="fm-ranked-list__list">
        {visible.map((item, index) => (
          <RankedRow
            key={item.key}
            rank={item.rank ?? index + 1}
            item={item}
            showIndicators={showIndicators}
            topShade={
              index < highlightTop
                ? Math.round((TOP_SHADE_MAX * (highlightTop - index)) / highlightTop)
                : undefined
            }
          />
        ))}
      </ol>
      {hiddenCount > 0 ? (
        <p className="fm-ranked-list__more">{`+${hiddenCount} more`}</p>
      ) : null}
    </div>
  );
};

/**
 * FmRankedList — a numbered, ranked list section.
 *
 * A numbered vertical list of rows, each with a rank number, an optional
 * change/status indicator, a label (+ optional sublabel), and a right-aligned
 * value — optionally paired beside a narrative panel. Generic: works for
 * "top revenue cutting opportunities", stands ranked by value, species by area,
 * etc. Fully tokenized; light default with a `tone="dark"` variant.
 */
export const FmRankedList: React.FC<FmRankedListProps> = ({
  items,
  limit,
  highlightTop = 0,
  eyebrow,
  title,
  headerSide = 'top',
  valueHeader,
  controls,
  narrative,
  narrativeSide = 'right',
  emptyLabel,
  tone = 'light',
  className,
  'data-testid': dataTestId,
}) => {
  const reactId = useId();
  const headerId = title ? `${reactId}-title` : undefined;

  const visible =
    limit !== undefined && limit >= 0 ? items.slice(0, limit) : items;
  const hiddenCount = items.length - visible.length;
  const showIndicators = visible.some((item) => item.indicator);
  const hasNarrative = narrative !== undefined && narrative !== null && narrative !== false;
  const headerLeft = headerSide === 'left';

  const rootClass = buildRankedListClass({
    tone,
    headerLeft,
    hasNarrative,
    narrativeSide,
    className,
  });

  const header = (
    <RankedHeader
      headerId={headerId}
      eyebrow={eyebrow}
      title={title}
      controls={controls}
    />
  );
  const listCol = (
    <RankedListCol
      visible={visible}
      hiddenCount={hiddenCount}
      valueHeader={valueHeader}
      emptyLabel={emptyLabel}
      showIndicators={showIndicators}
      highlightTop={highlightTop}
    />
  );
  const narrativePanel = hasNarrative ? (
    <aside className="fm-ranked-list__narrative">{narrative}</aside>
  ) : null;

  // header-left: header, list and narrative are all columns of one grid row.
  if (headerLeft) {
    return (
      <section className={rootClass} aria-labelledby={headerId} data-testid={dataTestId}>
        <div className="fm-ranked-list__body">
          {header}
          {listCol}
          {narrativePanel}
        </div>
      </section>
    );
  }

  return (
    <section className={rootClass} aria-labelledby={headerId} data-testid={dataTestId}>
      {header}
      <div className="fm-ranked-list__body">
        {listCol}
        {narrativePanel}
      </div>
    </section>
  );
};

export default FmRankedList;
